/**
 * The person's own AI on this machine, from the page's side — desktop only. See agents.rs.
 *
 * Every call answers "nothing here" in a browser rather than throwing, like lib/desktop.ts.
 */

import { isDesktop } from './desktop'
import { API_BASE } from './api'

/** The Vephus server this app talks to, for the agent's MCP and hooks: the key the agent gets is this server's. */
function vephusServer(): string | null {
  try {
    return new URL(API_BASE, window.location.href).origin
  } catch {
    return null
  }
}

/** The visible name of each agent the desktop can run or detect. */
export const AGENT_NAMES: Record<string, string> = {
  claude: 'Claude Code',
  'claude-2': 'Claude Code #2',
  codex: 'Codex CLI',
  gemini: 'Gemini CLI',
  opencode: 'OpenCode',
}

/** "claude-2" is the person's second Claude account on this computer (docs/refactor/60): Claude Code, signed in apart. */
export type LocalAgentId = 'claude' | 'claude-2' | 'codex' | 'gemini' | 'opencode'

/** Which program an agent id runs: the second Claude account is Claude Code too. */
export function agentProgram(agent: LocalAgentId): Exclude<LocalAgentId, 'claude-2'> {
  return agent === 'claude-2' ? 'claude' : agent
}
export type AiProvider = 'anthropic' | 'openai' | 'gemini'

/**
 * How much a headless agent may do without asking: edit the project's files only; everything in the project,
 * commands included; or the whole computer, programs and files outside the project included (docs/refactor/59).
 */
export type Permission = 'edits' | 'all' | 'computer'
export const PERMISSIONS: readonly Permission[] = ['edits', 'all', 'computer']

export interface DetectedAgent {
  id: LocalAgentId
  name: string
  installed: boolean
  /** Whether the app can run it headless for work orders today. */
  runnable: boolean
  version: string | null
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  return call<T>(command, args)
}

export async function detectAgents(): Promise<DetectedAgent[]> {
  if (!isDesktop()) return []
  return invoke<DetectedAgent[]>('agents_detect')
}

/** Opens a terminal with the agent's own sign-in. The app never sees the password. */
export async function agentLogin(agent: LocalAgentId): Promise<void> {
  await invoke('agent_login', { agent })
}

/** Sets up the person's second Claude account on this computer (docs/refactor/60); signing in comes next, as for the first. */
export async function agentAccountAdd(agent: LocalAgentId): Promise<void> {
  await invoke('agent_account_add', { agent })
}

/** Forgets the second Claude account: its sign-in leaves this computer. */
export async function agentAccountForget(agent: LocalAgentId): Promise<void> {
  await invoke('agent_account_forget', { agent })
}

export async function aiKeyStore(provider: AiProvider, key: string): Promise<void> {
  await invoke('ai_key_store', { provider, key })
}

/** Whether a key is stored for the provider — never the key. */
export async function aiKeyHas(provider: AiProvider): Promise<boolean> {
  if (!isDesktop()) return false
  return invoke<boolean>('ai_key_has', { provider })
}

export async function aiKeyClear(provider: AiProvider): Promise<void> {
  await invoke('ai_key_clear', { provider })
}

export interface RunHandlers {
  onLine: (line: string, stream: 'stdout' | 'stderr') => void
  onExit: (code: number | null) => void
}

/**
 * Runs the agent once in a picked folder with `prompt` on its stdin. Resolves when it has started;
 * `handlers` hear its output and its end. Returns the way to stop it.
 */
export async function runAgent(
  runId: string,
  agent: LocalAgentId,
  folder: string,
  prompt: string,
  permission: Permission,
  handlers: RunHandlers,
  /** A chat turn: the same conversation across turns, output as stream-json (Claude Code). */
  chat?: { sessionId: string; resume: boolean },
  /** The page reports this run to Vephus itself, so the agent's own hooks stay quiet (agents.rs). */
  options?: { reportedByApp?: boolean; system?: string; model?: string },
): Promise<() => Promise<void>> {
  // The free local AI's server (local_ai.rs) was only started by the installer: after a restart OpenCode
  // waited a minute and failed. Started first, at once when it already runs. Not installed: said now, not
  // after OpenCode's minute of retries.
  if (agent === 'opencode') {
    const started = await invoke('local_ai_start').then(() => null, (error: unknown) => String(error))
    if (started?.includes('ollama-not-installed')) throw new Error('local-ai-not-installed')
  }
  const { listen } = await import('@tauri-apps/api/event')
  const stopOutput = await listen<{ run_id: string; stream: 'stdout' | 'stderr'; line: string }>('devpulse://agent-output', (event) => {
    if (event.payload.run_id === runId) handlers.onLine(event.payload.line, event.payload.stream)
  })
  const stopExit = await listen<{ run_id: string; code: number | null }>('devpulse://agent-exit', (event) => {
    if (event.payload.run_id !== runId) return
    stopOutput()
    stopExit()
    handlers.onExit(event.payload.code)
  })

  try {
    await invoke('agent_run', { runId, agent, folder, prompt, permission, chat: chat ? { session_id: chat.sessionId, resume: chat.resume } : null, reportedByApp: options?.reportedByApp === true, system: options?.system || null, model: options?.model || null, apiUrl: vephusServer() })
  } catch (error) {
    stopOutput()
    stopExit()
    throw error
  }
  return () => invoke('agent_stop', { runId })
}

/**
 * How an agent is named to the Vephus API (the X-DevPulse-Provider header, telemetry): Claude Code is "claude-code",
 * every other agent its own id. One place, so a new agent cannot change what the existing ones send.
 */
export function providerSlug(agent: string): string {
  return agent === 'claude' || agent === 'claude-2' ? 'claude-code' : agent
}

